import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  {
    label: 'Services',
    path: '/services',
    children: [
      { label: 'Kitchen Remodeling', path: '/services/kitchen-remodeling' },
      { label: 'Bathroom Remodeling', path: '/services/bathroom-remodeling' },
      { label: 'Plumbing', path: '/services/plumbing' },
      { label: 'General Contracting', path: '/services/general-contracting' },
      { label: 'Deck Construction', path: '/services/deck-construction-repair' },
      { label: 'Cleaning Services', path: '/cleaning-services', badge: 'Additional Service' },
      { label: 'All Services', path: '/services' },
    ],
  },
  { label: 'Projects', path: '/projects' },
  { label: 'Reviews', path: '/reviews' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  const isServiceActive = location.pathname.startsWith('/services');

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60 transition-all duration-300"
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/images/LOGO-RAFAEL.webp"
              alt="Rafael M&C Constructions LLC"
              className="h-9 md:h-11 w-auto object-contain"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <Link
                    to={link.path}
                    className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                      isServiceActive
                        ? 'text-primary-600'
                        : 'text-foreground-800 hover:text-primary-600'
                    }`}
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${servicesOpen ? 'rotate-180' : ''}`}></i>
                  </Link>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-0 w-56 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="flex items-center px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 whitespace-nowrap transition-colors"
                        >
                          {child.label}
                          {child.badge && (
                            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-semibold uppercase tracking-wider leading-none">
                              {child.badge}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary-600'
                      : 'text-foreground-800 hover:text-primary-600'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
            >
              Free Estimate
              <i className="ri-arrow-right-line"></i>
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer text-foreground-700 hover:bg-background-200"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <Link
                    to={link.path}
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                  >
                    {link.label}
                  </Link>
                  <div className="ml-4 space-y-1">
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="flex items-center px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                      >
                        {child.label}
                        {child.badge && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-semibold uppercase tracking-wider leading-none">
                            {child.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
            <Link
              to="/contact"
              className="block w-full text-center px-4 py-3 mt-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Free Estimate
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}